import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";

const LEARNER_TYPES = [
  { value: "any", label: "All types" },
  { value: "programme", label: "Programme" },
  { value: "private", label: "Private" },
  { value: "qa", label: "QA" },
  { value: "unclassified", label: "Unclassified" },
];

const LINK_STATES = [
  { value: "any", label: "Any link state" },
  { value: "linked", label: "Linked (live mapping)" },
  { value: "unlinked", label: "Unlinked (GDI id, no mapping)" },
  { value: "unknown", label: "Unknown (no GDI id)" },
];

const LIFECYCLE_TABS = [
  { value: "any", label: "Any lifecycle" },
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On Hold" },
  { value: "completed", label: "Completed" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "archived", label: "Archived (operational)" },
  { value: "review", label: "Review" },
];

export default function AdminLearnerOversight() {
  const [instructors, setInstructors] = useState([]);
  const [learners, setLearners] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [instructorId, setInstructorId] = useState("");
  const [learnerType, setLearnerType] = useState("any");
  const [lifecycleTab, setLifecycleTab] = useState("any");
  const [linkState, setLinkState] = useState("any");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [identityQueue, setIdentityQueue] = useState([]);

  useEffect(() => {
    api.get("/admin/instructors").then((res) => setInstructors(res.data.instructors || [])).catch(() => {});
    api
      .get("/admin/identity-reconciliation", { params: { status: "open", limit: 25 } })
      .then((res) => setIdentityQueue(res.data.items || []))
      .catch(() => setIdentityQueue([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const params = { page, page_size: pageSize };
    if (instructorId) params.instructor_id = instructorId;
    if (learnerType !== "any") params.learner_type = learnerType;
    if (lifecycleTab !== "any") params.operational_lifecycle_tab = lifecycleTab;
    if (linkState !== "any") params.link_state = linkState;
    api
      .get("/admin/learner-oversight", { params })
      .then((res) => {
        setLearners(res.data.learners || []);
        setPagination(res.data.pagination || null);
        setSummary(res.data.summary || null);
      })
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [page, pageSize, instructorId, learnerType, lifecycleTab, linkState]);

  useEffect(() => {
    load();
  }, [load]);

  const total = pagination?.total_distinct_learners ?? 0;

  return (
    <div className="space-y-6" data-testid="admin-learner-oversight-page">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold text-primary">Admin — Learner oversight</h1>
        <p className="text-sm text-muted-foreground">
          Distinct Academy learner accounts (not assignment-row totals). Operational lifecycle and manual archive are
          shown per instructor assignment.{" "}
          <Link to="/admin" className="underline">Lesson admin</Link>
          {" · "}
          <Link to="/admin/training-requests" className="underline">Training requests</Link>
          {" · "}
          <Link to="/admin/instructors" className="underline">Instructor onboarding</Link>
        </p>
      </header>

      {identityQueue.length > 0 && (
        <section className="card space-y-2 border-amber-500/40" data-testid="admin-identity-reconciliation-queue">
          <h2 className="text-lg font-semibold">Identity reconciliation (action required)</h2>
          <p className="text-sm text-muted-foreground">
            CRM email or stable-id conflicts that cannot be linked automatically. Resolve manually; do not merge accounts.
          </p>
          <ul className="text-sm space-y-2">
            {identityQueue.map((item) => (
              <li key={item.id} data-testid={`identity-recon-${item.id}`}>
                <span className="font-medium">{item.reason}</span>
                {" — "}
                {item.learner?.email || item.email}
                {item.learner?.display_name ? ` (${item.learner.display_name})` : ""}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="card space-y-3" data-testid="admin-oversight-filters">
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm space-y-1">
            <span className="font-medium">Instructor scope</span>
            <select
              className="input"
              data-testid="admin-oversight-instructor-filter"
              value={instructorId}
              onChange={(e) => {
                setPage(1);
                setInstructorId(e.target.value);
              }}
            >
              <option value="">Global (all relationships)</option>
              {instructors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.display_name || i.email || i.id}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">Learner type</span>
            <select
              className="input"
              value={learnerType}
              onChange={(e) => {
                setPage(1);
                setLearnerType(e.target.value);
              }}
            >
              {LEARNER_TYPES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">Operational lifecycle</span>
            <select
              className="input"
              data-testid="admin-oversight-lifecycle-filter"
              value={lifecycleTab}
              onChange={(e) => {
                setPage(1);
                setLifecycleTab(e.target.value);
              }}
            >
              {LIFECYCLE_TABS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">GDI link</span>
            <select
              className="input"
              value={linkState}
              onChange={(e) => {
                setPage(1);
                setLinkState(e.target.value);
              }}
            >
              {LINK_STATES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">Page size</span>
            <select
              className="input"
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
        {instructorId && (
          <p className="text-sm">
            <Link
              to={`/instructor?as_instructor_id=${encodeURIComponent(instructorId)}`}
              className="underline"
              data-testid="admin-open-instructor-roster-link"
            >
              Open instructor roster view (authorized selection)
            </Link>
          </p>
        )}
      </div>

      {summary && (
        <p className="text-sm" data-testid="admin-oversight-summary">
          <strong>{total}</strong> distinct learners match filters.
          {summary.operational_lifecycle_tab_counts && instructorId && (
            <span>
              {" "}
              Instructor-scoped tabs: Active {summary.operational_lifecycle_tab_counts.active}, All{" "}
              {summary.operational_lifecycle_tab_counts.all}.
            </span>
          )}
        </p>
      )}

      {error && <p role="alert" className="text-destructive">{error}</p>}
      {loading && <p data-testid="admin-oversight-loading">Loading…</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-oversight-table">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2 pr-2">Name</th>
              <th className="py-2 pr-2">Type</th>
              <th className="py-2 pr-2">GDI link</th>
              <th className="py-2 pr-2">Account</th>
              <th className="py-2 pr-2">Enrolment</th>
              <th className="py-2 pr-2">Access</th>
              <th className="py-2">Instructor relationships</th>
            </tr>
          </thead>
          <tbody>
            {learners.map((row) => (
              <tr key={row.id} className="border-b align-top" data-testid={`admin-oversight-row-${row.id}`}>
                <td className="py-2 pr-2">
                  <div>{row.display_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{row.email}</div>
                </td>
                <td className="py-2 pr-2">{row.learner_type}</td>
                <td className="py-2 pr-2">
                  <span data-testid={`admin-oversight-link-${row.id}`}>{row.gdi_link_state}</span>
                </td>
                <td className="py-2 pr-2">{row.academy_account_status || "—"}</td>
                <td className="py-2 pr-2">{row.enrolment_status || "—"}</td>
                <td className="py-2 pr-2">{row.training_entitlement || "—"}</td>
                <td className="py-2 text-xs space-y-1">
                  {(row.instructor_relationships || []).length === 0 ? (
                    <span className="text-muted-foreground">No active assignment</span>
                  ) : (
                    row.instructor_relationships.map((rel) => (
                      <div key={`${rel.assignment_id}-${rel.instructor_id}`}>
                        <strong>{rel.instructor_display_name || rel.instructor_id?.slice(0, 8)}</strong>
                        {" · "}
                        Op: {rel.operational_status_label || "—"} ({rel.operational_lifecycle_tab})
                        {rel.manual_roster_archived ? " · Archived (my list)" : ""}
                        {" · "}
                        {rel.assignment_status}
                      </div>
                    ))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.total_pages > 1 && (
        <div className="flex gap-2 items-center" data-testid="admin-oversight-pagination">
          <button type="button" className="btn-outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span className="text-sm">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <button
            type="button"
            className="btn-outline"
            disabled={page >= pagination.total_pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
